/**
 * PostTripSummaryCard.jsx — v1.0.0 Gate 4 Item 3
 *
 * Appears during post_birthday_7 state above the survey prompt.
 * Shows: offers saved during birthday season, points earned birthday month,
 * badges earned birthday month, contextual copy-bank message.
 */
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Sparkles, Bookmark, Star, Award } from "lucide-react";
import { API, axios } from "@/api";
import { useUserState } from "@/contexts/UserStateContext";

export default function PostTripSummaryCard() {
  const { state } = useUserState();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (state !== "post_birthday_7") return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/post-trip-summary`);
        if (!cancelled) setData(res.data);
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [state]);

  if (state !== "post_birthday_7" || !data) return null;

  const { offers_saved_count = 0, points_earned = 0, badges_earned_count = 0, copy_bank_text = "" } = data;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="mx-4 mt-3 mb-2 rounded-2xl overflow-hidden border border-indigo-100"
      style={{
        background: "linear-gradient(135deg, #f5f3ff 0%, #ecfeff 100%)",
      }}
      data-testid="post-trip-summary-card"
    >
      <div className="px-4 pt-3 pb-2 flex items-center gap-2">
        <Sparkles className="w-3.5 h-3.5 text-indigo-500" />
        <span className="text-[10px] font-bold text-indigo-600 tracking-widest uppercase">
          Your birthday season
        </span>
      </div>
      <div className="px-4 grid grid-cols-3 gap-2 pb-3">
        <StatCell
          icon={Bookmark}
          value={offers_saved_count}
          label="Saved"
          testId="pts-offers-saved"
        />
        <StatCell
          icon={Star}
          value={points_earned}
          label="Points"
          testId="pts-points-earned"
        />
        <StatCell
          icon={Award}
          value={badges_earned_count}
          label="Badges"
          testId="pts-badges-earned"
        />
      </div>
      {copy_bank_text && (
        <div className="px-4 pb-4">
          <p
            className="text-xs text-slate-600 italic leading-snug"
            data-testid="pts-copy-bank-text"
          >
            "{copy_bank_text}"
          </p>
        </div>
      )}
    </motion.div>
  );
}

function StatCell({ icon: Icon, value, label, testId }) {
  return (
    <div className="rounded-xl bg-white/70 p-2.5 text-center" data-testid={testId}>
      <Icon className="w-3.5 h-3.5 text-slate-400 mx-auto" />
      <p className="text-lg font-black text-slate-800 mt-1 leading-none">{value}</p>
      <p className="text-[10px] text-slate-500 mt-0.5">{label}</p>
    </div>
  );
}
